import { Component, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { CommonModule } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { RuntimeService } from '../../shared/services/runtime.service';
import { NetworkStatusService } from '../../shared/services/network-status.service';
import { ToastrService } from 'ngx-toastr';
import { NetworkStatusBadgeComponent } from '../../shared/network-status-badge/network-status-badge.component';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, CommonModule, RouterLink, NetworkStatusBadgeComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  loading = false;
  errorMessage = '';
  fb = new FormBuilder();
  hidePassword = true;
  isOnline: Signal<boolean>;
  private pendingToast: { message: string; title?: string } | null = null;

  loginForm = this.fb.group({
    username: [
      '',
      [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/)
      ]
    ],
    password: [
      '',
      [
        Validators.required
      ]
    ]
  });

  constructor(
    private authService: AuthService,
    private router: Router,
    private spinner: NgxSpinnerService,
    protected runtime: RuntimeService,
    private networkService: NetworkStatusService,
    private toast: ToastrService
  ) {
    this.isOnline = toSignal(this.networkService.isOnline$, {
      initialValue: this.networkService.isOnline,
    });
    const toastState = this.router.getCurrentNavigation()?.extras?.state?.['toast']
      ?? history.state?.toast;
    this.pendingToast = toastState?.message ? toastState : null;
  }

  ngOnInit(): void {
    localStorage.removeItem('session_expired');
    if (this.pendingToast?.message) {
      this.toast.success(
        this.pendingToast.message,
        this.pendingToast.title || 'Success',
        { onActivateTick: true }
      );
      this.pendingToast = null;
    }
  }

  // Only devices that have never completed the first-time TOTP setup need network
  // enforcement here — a returning login on an already-provisioned device is free
  // to attempt offline (periodic revalidation covers the session once signed in).
  get isFirstTimeSetupPending(): boolean {
    return this.runtime.IsDesktopApplication && !this.authService.hasCompletedFirstTimeSetup();
  }

  submit() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    if (this.isFirstTimeSetupPending && !this.isOnline()) {
      return;
    }

    this.loading = true;
    this.spinner.show();
    this.errorMessage = '';

    this.authService.login(this.loginForm.value).subscribe({
      next: () => {
        this.loading = false;
        this.spinner.hide();

        const mustReset = localStorage.getItem('reset_password_required') === 'true';
        if (mustReset) {
          this.router.navigate(['/reset-password']);
        } else if (this.runtime.IsDesktopApplication) {
          this.router.navigate(['/authenticate'], { replaceUrl: true });
        } else {
          this.router.navigate(['/load-planning/flight-list'], { replaceUrl: true });
        }
      }, error: () => {
        this.loading = false;
        this.spinner.hide();
      }
    });
  }

}
